import { esriRequest } from '../constructors';
import { logMapInfo, logMapWarn } from './utils';
import {
    CachedResponseType,
    MapTileCacheRecord,
    MapTileCacheStore,
    deleteMapTileCacheDatabase,
    mapTileCacheDatabaseExists,
    openMapTileCacheStore,
} from './map-tile-cache-store';

/**
 * A local copy of the Esri basemap, so a map that opens again draws from disk instead of
 * downloading it all, and keeps drawing when the network is bad. Behind
 * `cad.map_tile_cache.enabled`.
 *
 * WHY. Destroying a MapView destroys its basemap too, so every rebuild fetches the whole
 * chain again, one step after another: the basemap definition, two styles, the service
 * description, then sprites, glyphs, tilemaps and tiles. The browser's HTTP cache doesn't
 * cover it. cad-electron clears that cache when the frontend changes, every URL carries a
 * token so a new token misses, Esri's max-ages are short (5 minutes for sprites, none for
 * our label fonts), and once one runs out a dead network fails the request instead of
 * falling back to the copy the browser already has.
 *
 * HOW. esri/request runs `interceptors[].before` ahead of every request, and if it returns
 * anything but null/undefined, that becomes the response and the network is never touched.
 * `after` sees every successful response. So `before` answers from IndexedDB and `after`
 * fills it. Two traps in that contract shape everything below:
 *   - a throw from either of them (or an Error returned from `before`) fails the Esri
 *     request, so every path is wrapped and any failure falls through to the network;
 *   - `after` is called synchronously and not awaited, so it copies the data straight away
 *     and writes in the background.
 *
 * FRESHNESS. A copy under 3 days old is served. Each copy is also refreshed in the
 * background once per app load, the first time it's served, so the next map open gets
 * whatever Esri has published since; the map on screen is never swapped mid-view. The
 * refreshes wait until a map has finished drawing, so they never compete with it. A sweep
 * deletes copies older than 7 days and keeps the total under 200 MB.
 *
 * Logs carry counts only. Never a URL, cache key or tile coordinate: tile coordinates say
 * where someone was looking.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** A copy at least this old goes back to the network instead of being served. */
const SERVE_MAX_AGE_MS = 3 * DAY_MS;
/** The sweep deletes copies older than this. */
const PURGE_AFTER_MS = 7 * DAY_MS;
/** A backstop: with the 7-day purge a department's working set is normally well under it. */
const MAX_BYTES = 200 * 1024 * 1024;
/** A lookup slower than this counts as a miss, so a stuck database can't stall the map. */
const LOOKUP_TIMEOUT_MS = 250;
/** `lastUsedAt` only matters to eviction, so a hit rewrites it at most this often. */
const TOUCH_INTERVAL_MS = HOUR_MS;
const MAX_CONCURRENT_REFRESHES = 4;
const MAX_QUEUED_REFRESHES = 500;
const REFRESH_TIMEOUT_MS = 30_000;
/** Refreshes wait for a map to finish drawing, but go out after this long in case none does. */
const REFRESH_RELEASE_FALLBACK_MS = 30_000;
/** Consecutive storage failures that turn the cache off for the rest of the app load. */
const FAILURES_BEFORE_DISABLE = 5;
const ACTIVITY_REPORT_INTERVAL_MS = 15 * MINUTE_MS;
const SWEEP_IDLE_TIMEOUT_MS = 10_000;

/**
 * What gets cached: Esri's public basemap, and nothing else. A department's own webmap and
 * feature data never match.
 */
export const MAP_TILE_CACHE_URLS: RegExp[] = [
    // Basemap definitions, and the vector tile styles they point to, from the basemap styles
    // service.
    /^https:\/\/basemapstyles-api\.arcgis\.com\/arcgis\/rest\/services\/styles\/v2\/(webmaps|layers)\//,
    // Vector tile styles and their sprites, from Esri's own content items.
    /^https:\/\/(cdn|www)\.arcgis\.com\/sharing\/rest\/content\/items\/[0-9a-f]{32}\/resources\/(styles|sprites)\//,
    // The vector tile service: its description, tilemaps, tiles and glyphs.
    // basemaps.arcgis.com serves the alternative basemaps.
    /^https:\/\/basemaps(-api)?\.arcgis\.com\/arcgis\/rest\/services\/[^/]+\/VectorTileServer(\/|\?|$)/,
    // Glyphs for our own map labels, and the basemap's attribution text.
    /^https:\/\/static\.arcgis\.com\/(fonts|attribution)\//,
];

/**
 * Marks our own background refreshes, so `before` lets them through to the network and
 * their failures stay out of basemap error handling. esri/request copies the options it is
 * given (`{ ...options }`), which keeps symbol keys.
 */
const REFRESH_MARKER: unique symbol = Symbol('map-tile-cache-refresh');

type InterceptedRequest = {
    url: string;
    requestOptions?: __esri.RequestOptions;
};

export type CacheableRequest = {
    key: string;
    responseType: CachedResponseType;
    /** Never stored or logged: swapped out of JSON before storing, back in when serving. */
    token: string | undefined;
};

type CapturedBody = { body: unknown; bytes: number };

type RefreshJob = {
    url: string;
    query: Record<string, unknown> | undefined;
    responseType: CachedResponseType;
    /** What was served, so the refresh can tell whether Esri had something newer. */
    servedFingerprint: string | undefined;
};

type CacheOperation = 'open' | 'get' | 'put' | 'touch' | 'evict' | 'sweep';
type DisableReason = 'storage_failures' | 'lookup_timeouts' | 'unavailable';

type Counters = {
    hits: number;
    misses: number;
    expired: number;
    lookupTimeouts: number;
    writes: number;
    writesSkipped: number;
    writeFailures: number;
    bytesServed: number;
    bytesWritten: number;
    refreshesStarted: number;
    refreshesSucceeded: number;
    refreshesChanged: number;
    refreshesFailed: number;
    refreshesSkippedOffline: number;
    evicted: number;
};

function emptyCounters(): Counters {
    return {
        hits: 0,
        misses: 0,
        expired: 0,
        lookupTimeouts: 0,
        writes: 0,
        writesSkipped: 0,
        writeFailures: 0,
        bytesServed: 0,
        bytesWritten: 0,
        refreshesStarted: 0,
        refreshesSucceeded: 0,
        refreshesChanged: 0,
        refreshesFailed: 0,
        refreshesSkippedOffline: 0,
        evicted: 0,
    };
}

// Module state lives for the app load. Several windows share the database, but each window
// keeps its own counters, refresh bookkeeping and breaker.
const counters = emptyCounters();
let lastReportedCounters = emptyCounters();
let installed = false;
let disabledReason: DisableReason | undefined;
let consecutiveFailures = 0;
let storePromise: Promise<MapTileCacheStore | undefined> | undefined;
let storeReady = false;
const refreshedThisLoad = new Set<string>();
const refreshQueue: RefreshJob[] = [];
let refreshesInFlight = 0;
let refreshesReleased = false;
let refreshReleaseTimer: ReturnType<typeof setTimeout> | undefined;
let sweepScheduled = false;
let clearedThisLoad = false;

function isActive(): boolean {
    return installed && disabledReason === undefined;
}

function isOnline(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function isArrayBuffer(value: unknown): value is ArrayBuffer {
    return Object.prototype.toString.call(value) === '[object ArrayBuffer]';
}

/** DOMException names such as `QuotaExceededError`. Never a message, which could say anything. */
function errorName(error: unknown): string {
    const name = (error as { name?: unknown } | null | undefined)?.name;
    return typeof name === 'string' && /^[A-Za-z]{1,64}$/.test(name) ? name : 'Unknown';
}

function isRefreshRequest(options: unknown): boolean {
    return (
        typeof options === 'object' &&
        options !== null &&
        (options as { [REFRESH_MARKER]?: unknown })[REFRESH_MARKER] === true
    );
}

function compareStrings(a: string, b: string): number {
    if (a === b) {
        return 0;
    }
    return a < b ? -1 : 1;
}

/**
 * Whether a request is one the cache handles, and the key it is stored under: the response
 * type plus the URL with its query merged in, sorted, and the token removed. Without the
 * token in the key, a copy outlives token rotation and a new launch.
 */
export function describeCacheableRequest(
    url: string,
    options: __esri.RequestOptions | undefined
): CacheableRequest | undefined {
    if (!MAP_TILE_CACHE_URLS.some((pattern) => pattern.test(url))) {
        return undefined;
    }
    if (
        options?.body !== undefined ||
        (options?.method !== undefined && options.method !== 'auto')
    ) {
        return undefined;
    }
    const responseType = options?.responseType ?? 'json';
    if (responseType !== 'json' && responseType !== 'array-buffer' && responseType !== 'image') {
        return undefined;
    }

    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return undefined;
    }

    let token: string | undefined;
    const params: Array<[string, string]> = [];
    const addParam = (name: string, value: string) => {
        if (name.toLowerCase() === 'token') {
            token = value;
        } else {
            params.push([name, value]);
        }
    };
    parsed.searchParams.forEach((value, name) => addParam(name, value));

    const query: unknown = options?.query;
    if (query !== undefined && query !== null) {
        if (typeof query !== 'object') {
            return undefined;
        }
        for (const [name, value] of Object.entries(query as Record<string, unknown>)) {
            if (value === undefined || value === null) {
                continue;
            }
            if (
                typeof value !== 'string' &&
                typeof value !== 'number' &&
                typeof value !== 'boolean'
            ) {
                // Not a shape our basemap requests use; better uncached than a wrong key.
                return undefined;
            }
            addParam(name, String(value));
        }
    }

    params.sort(([aName, aValue], [bName, bValue]) =>
        aName === bName ? compareStrings(aValue, bValue) : compareStrings(aName, bName)
    );
    const search = new URLSearchParams(params).toString();
    return {
        key: `${responseType} ${parsed.origin}${parsed.pathname}${search ? `?${search}` : ''}`,
        responseType,
        token,
    };
}

function hashBytes(bytes: Uint8Array): number {
    let hash = 0x811c9dc5;
    for (let index = 0; index < bytes.length; index++) {
        hash ^= bytes[index];
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}

function hashString(text: string): number {
    let hash = 0x811c9dc5;
    for (let index = 0; index < text.length; index++) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}

/** Enough to tell whether a refresh brought back something different. Images can't say. */
function fingerprint(data: unknown, responseType: CachedResponseType): string | undefined {
    try {
        if (responseType === 'array-buffer' && isArrayBuffer(data)) {
            return `${data.byteLength}:${hashBytes(new Uint8Array(data))}`;
        }
        if (responseType === 'json') {
            const text = JSON.stringify(data);
            return text === undefined ? undefined : `${text.length}:${hashString(text)}`;
        }
    } catch {
        // No fingerprint just means the refresh can't be counted as changed or not.
    }
    return undefined;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | 'timed-out'> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<'timed-out'>((resolve) => {
        timer = setTimeout(() => resolve('timed-out'), timeoutMs);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function noteSuccess(): void {
    consecutiveFailures = 0;
}

function disable(reason: DisableReason): void {
    if (disabledReason !== undefined) {
        return;
    }
    disabledReason = reason;
    refreshQueue.length = 0;
    try {
        logMapWarn({
            source: 'cache',
            event: 'cache_disabled',
            extraData: { reason, failureCount: consecutiveFailures },
        });
    } catch {
        // Logging must never fail the map.
    }
}

function noteFailure(
    operation: CacheOperation,
    error: unknown,
    reason: DisableReason = 'storage_failures'
): void {
    consecutiveFailures++;
    try {
        logMapWarn({
            source: 'cache',
            event: 'cache_error',
            extraData: { operation, errorName: errorName(error), consecutiveFailures },
        });
    } catch {
        // Logging must never fail the map.
    }
    if (consecutiveFailures >= FAILURES_BEFORE_DISABLE) {
        disable(reason);
    }
}

function getStore(): Promise<MapTileCacheStore | undefined> {
    storePromise ??= openMapTileCacheStore().then(
        (store) => {
            storeReady = true;
            return store;
        },
        (error: unknown) => {
            // With no database there is nothing to cache into, so stop asking for this load.
            noteFailure('open', error);
            disable('unavailable');
            return undefined;
        }
    );
    return storePromise;
}

async function lookup(key: string): Promise<MapTileCacheRecord | undefined> {
    let failed = false;
    const read = getStore()
        .then((store) => store?.get(key))
        .catch((error: unknown) => {
            failed = true;
            noteFailure('get', error);
            return undefined;
        });
    const result = await withTimeout(read, LOOKUP_TIMEOUT_MS);
    if (result === 'timed-out') {
        // Only a slow read counts against the cache. A database still opening at startup
        // is expected, and the open has its own timeout.
        if (storeReady) {
            counters.lookupTimeouts++;
            noteFailure(
                'get',
                new DOMException('Lookup timed out', 'TimeoutError'),
                'lookup_timeouts'
            );
        }
        return undefined;
    }
    if (!failed && storeReady) {
        // A miss is a successful read too.
        noteSuccess();
    }
    return result;
}

/**
 * Stands in for the request's token in a stored copy. The basemap styles service writes the
 * token into the styles it returns (in their font URLs), so a copy of one is kept with the token
 * taken out, and the current request's token is put back when it is served. A token is never
 * stored, so a copy can't hand Esri a stale one.
 */
const TOKEN_PLACEHOLDER = 'M43_MAP_TILE_CACHE_TOKEN';
/** A `token=` parameter holding anything but the placeholder: a token we can't put back. */
const UNSWAPPABLE_TOKEN_PARAM = new RegExp(`[?&]token=(?!${TOKEN_PLACEHOLDER})`, 'i');
/** A shorter token could also be ordinary text in the response. Esri's are far longer. */
const MIN_SWAPPABLE_TOKEN_LENGTH = 20;

function isSwappableToken(token: string | undefined): token is string {
    return token !== undefined && token.length >= MIN_SWAPPABLE_TOKEN_LENGTH;
}

/** The JSON text to store, with the request's token swapped out, or undefined to skip it. */
function swapOutToken(text: string, token: string | undefined): string | undefined {
    const swapped = isSwappableToken(token) ? text.split(token).join(TOKEN_PLACEHOLDER) : text;
    if (UNSWAPPABLE_TOKEN_PARAM.test(swapped) || (token !== undefined && swapped.includes(token))) {
        return undefined;
    }
    return swapped;
}

/**
 * A stored JSON copy with the request's token put back where one was taken out. Undefined if the
 * copy needs a token and the request has none.
 */
function swapInToken(body: unknown, token: string | undefined): unknown {
    const text = JSON.stringify(body);
    if (text === undefined || !text.includes(TOKEN_PLACEHOLDER)) {
        return body;
    }
    return isSwappableToken(token)
        ? (JSON.parse(text.split(TOKEN_PLACEHOLDER).join(token)) as unknown)
        : undefined;
}

async function blobToImage(blob: Blob): Promise<HTMLImageElement | undefined> {
    let url: string | undefined;
    try {
        url = URL.createObjectURL(blob);
        const image = new Image();
        image.src = url;
        await image.decode();
        return image;
    } catch {
        return undefined;
    } finally {
        if (url !== undefined) {
            URL.revokeObjectURL(url);
        }
    }
}

/** The stored body in the shape esri/request would have produced for this response type. */
async function decodeBody(record: MapTileCacheRecord, token: string | undefined): Promise<unknown> {
    switch (record.responseType) {
        case 'array-buffer':
            return isArrayBuffer(record.body) ? record.body : undefined;
        case 'json':
            // IndexedDB hands back a fresh copy on every read, so the SDK can't alter ours.
            return swapInToken(record.body ?? undefined, token);
        case 'image':
            return record.body instanceof Blob ? blobToImage(record.body) : undefined;
        default:
            return undefined;
    }
}

function markUsed(record: MapTileCacheRecord, now: number): void {
    if (now - record.lastUsedAt < TOUCH_INTERVAL_MS) {
        return;
    }
    getStore()
        .then((store) => store?.touch(record.key, now))
        .then(noteSuccess, (error: unknown) => noteFailure('touch', error));
}

async function refresh(job: RefreshJob): Promise<void> {
    try {
        const options: __esri.RequestOptions & { [REFRESH_MARKER]: true } = {
            responseType: job.responseType,
            query: job.query,
            // Fetch with `cache: 'no-cache'`: the browser revalidates any copy it still has,
            // so an unchanged file costs a 304.
            cacheBust: true,
            timeout: REFRESH_TIMEOUT_MS,
            [REFRESH_MARKER]: true,
        };
        // Through esri/request rather than fetch, to keep Esri's token, OAuth and retry
        // handling. `after` stores what comes back.
        const response = await esriRequest(job.url, options);
        counters.refreshesSucceeded++;
        if (
            job.servedFingerprint !== undefined &&
            fingerprint(response.data, job.responseType) !== job.servedFingerprint
        ) {
            counters.refreshesChanged++;
        }
    } catch {
        // The copy we have stays; the next app load tries again.
        counters.refreshesFailed++;
    }
}

function pumpRefreshes(): void {
    while (
        isActive() &&
        refreshesReleased &&
        refreshesInFlight < MAX_CONCURRENT_REFRESHES &&
        refreshQueue.length > 0
    ) {
        const job = refreshQueue.shift() as RefreshJob;
        refreshesInFlight++;
        counters.refreshesStarted++;
        refresh(job).finally(() => {
            refreshesInFlight--;
            pumpRefreshes();
        });
    }
}

/** Lets this load's refreshes go out. Until then they queue, so the first map draws undisturbed. */
function releaseRefreshes(): void {
    refreshesReleased = true;
    clearTimeout(refreshReleaseTimer);
    refreshReleaseTimer = undefined;
    pumpRefreshes();
}

function queueRefreshOnFirstUse(
    request: CacheableRequest,
    params: InterceptedRequest,
    servedData: unknown
): void {
    if (refreshedThisLoad.has(request.key)) {
        return;
    }
    if (!isOnline()) {
        // Left unmarked, so a later use this load can still refresh it.
        counters.refreshesSkippedOffline++;
        return;
    }
    if (refreshQueue.length >= MAX_QUEUED_REFRESHES) {
        return;
    }
    refreshedThisLoad.add(request.key);
    const query: unknown = params.requestOptions?.query;
    refreshQueue.push({
        url: params.url,
        query:
            typeof query === 'object' && query !== null
                ? { ...(query as Record<string, unknown>) }
                : undefined,
        responseType: request.responseType,
        // Taken before the data is handed back: the SDK may pass a buffer to a worker.
        servedFingerprint: fingerprint(servedData, request.responseType),
    });
    if (!refreshesReleased) {
        // Normally released sooner, by the first map to finish drawing (notifyMapDrawn).
        refreshReleaseTimer ??= setTimeout(releaseRefreshes, REFRESH_RELEASE_FALLBACK_MS);
    }
    pumpRefreshes();
}

async function answerFromCache(params: InterceptedRequest): Promise<unknown> {
    try {
        if (!isActive() || isRefreshRequest(params.requestOptions)) {
            return undefined;
        }
        const request = describeCacheableRequest(params.url, params.requestOptions);
        if (!request) {
            return undefined;
        }
        const record = await lookup(request.key);
        if (!record) {
            counters.misses++;
            return undefined;
        }
        const now = Date.now();
        if (now - record.storedAt >= SERVE_MAX_AGE_MS) {
            counters.expired++;
            return undefined;
        }
        const data = await decodeBody(record, request.token);
        if (data === undefined) {
            counters.misses++;
            return undefined;
        }
        counters.hits++;
        counters.bytesServed += record.bytes;
        markUsed(record, now);
        queueRefreshOnFirstUse(request, params, data);
        return data;
    } catch {
        return undefined;
    }
}

function captureImage(data: unknown): Promise<CapturedBody | undefined> | undefined {
    if (
        typeof HTMLImageElement === 'undefined' ||
        !(data instanceof HTMLImageElement) ||
        !data.complete ||
        data.naturalWidth === 0
    ) {
        return undefined;
    }
    // Drawn now, while the image is known to be complete; encoded in the background. Sprites
    // are PNG, so re-encoding as PNG loses nothing.
    const canvas = document.createElement('canvas');
    canvas.width = data.naturalWidth;
    canvas.height = data.naturalHeight;
    const context = canvas.getContext('2d');
    if (!context) {
        return undefined;
    }
    context.drawImage(data, 0, 0);
    return new Promise((resolve) => {
        canvas.toBlob(
            (blob) => resolve(blob ? { body: blob, bytes: blob.size } : undefined),
            'image/png'
        );
    });
}

/** Copies the response synchronously and returns the copy, or undefined to skip it. */
function captureBody(
    data: unknown,
    request: CacheableRequest
): Promise<CapturedBody | undefined> | undefined {
    switch (request.responseType) {
        case 'array-buffer': {
            if (!isArrayBuffer(data) || data.byteLength === 0) {
                return undefined;
            }
            // Copied now: the SDK may hand this buffer to a worker, which empties it.
            const body = data.slice(0);
            return Promise.resolve({ body, bytes: body.byteLength });
        }
        case 'json': {
            const text = JSON.stringify(data);
            const stored = text === undefined ? undefined : swapOutToken(text, request.token);
            if (stored === undefined) {
                return undefined;
            }
            return Promise.resolve({ body: JSON.parse(stored) as unknown, bytes: stored.length });
        }
        case 'image':
            return captureImage(data);
        default:
            return undefined;
    }
}

function shrinkAfterQuotaError(): void {
    getStore()
        .then((store) => store?.shrink(0.5))
        .then(
            (result) => {
                counters.evicted += result?.evicted ?? 0;
            },
            (error: unknown) => noteFailure('evict', error)
        );
}

async function persist(
    request: CacheableRequest,
    capture: Promise<CapturedBody | undefined>
): Promise<void> {
    let captured: CapturedBody | undefined;
    try {
        captured = await capture;
    } catch {
        captured = undefined;
    }
    if (!captured) {
        counters.writesSkipped++;
        return;
    }
    try {
        const store = await getStore();
        if (!store || !isActive()) {
            return;
        }
        const now = Date.now();
        const { evicted } = await store.put(
            {
                key: request.key,
                responseType: request.responseType,
                body: captured.body,
                bytes: captured.bytes,
                storedAt: now,
                lastUsedAt: now,
            },
            MAX_BYTES
        );
        counters.writes++;
        counters.bytesWritten += captured.bytes;
        counters.evicted += evicted;
        noteSuccess();
    } catch (error) {
        counters.writeFailures++;
        noteFailure('put', error);
        if (errorName(error) === 'QuotaExceededError') {
            shrinkAfterQuotaError();
        }
    }
}

function storeFromNetwork(response: __esri.RequestResponse): void {
    try {
        if (!isActive()) {
            return;
        }
        const request = describeCacheableRequest(response.url ?? '', response.requestOptions);
        if (!request) {
            return;
        }
        if (/no-store/i.test(response.getHeader?.('cache-control') ?? '')) {
            counters.writesSkipped++;
            return;
        }
        const capture = captureBody(response.data, request);
        if (!capture) {
            counters.writesSkipped++;
            return;
        }
        // A copy fresh from the network needs no refresh this load.
        refreshedThisLoad.add(request.key);
        void persist(request, capture);
    } catch {
        // A copy that can't be kept must never fail the request it came from.
    }
}

/** esri/request's own URL matching (`core/urlUtils` getInterceptor). */
function interceptorMatches(urls: __esri.RequestInterceptor['urls'], url: string): boolean {
    const matches = (pattern: string | RegExp | undefined) =>
        pattern === undefined ||
        (pattern instanceof RegExp ? pattern.test(url) : url.startsWith(pattern));
    return Array.isArray(urls) ? urls.some(matches) : matches(urls);
}

function routeError(error: __esri.Error, delegate: __esri.RequestInterceptor | undefined): void {
    try {
        const details = error?.details as { url?: unknown; requestOptions?: unknown } | undefined;
        // A background refresh failing leaves the copy we have. It isn't a basemap failure,
        // and must not start a token refresh.
        if (isRefreshRequest(details?.requestOptions)) {
            return;
        }
        const url = details?.url;
        if (delegate?.error && typeof url === 'string' && interceptorMatches(delegate.urls, url)) {
            delegate.error(error);
        }
    } catch {
        // Error handling must never fail the request either.
    }
}

/**
 * `hits` becomes `cacheHits`. Datadog log attributes, and the measures built on them, are
 * shared across the whole org, so generic names like `hits` or `purged` get a prefix.
 */
function asLogFields(values: Record<string, number>): Record<string, number> {
    const fields: Record<string, number> = {};
    for (const [name, value] of Object.entries(values)) {
        fields[`cache${name.charAt(0).toUpperCase()}${name.slice(1)}`] = value;
    }
    return fields;
}

function reportActivity(): void {
    try {
        const delta = emptyCounters();
        let anything = false;
        for (const name of Object.keys(delta) as Array<keyof Counters>) {
            delta[name] = counters[name] - lastReportedCounters[name];
            anything = anything || delta[name] !== 0;
        }
        lastReportedCounters = { ...counters };
        if (anything) {
            logMapInfo({
                source: 'cache',
                event: 'cache_activity',
                extraData: asLogFields(delta),
            });
        }
    } catch {
        // Logging must never fail the map.
    }
}

async function estimateStorage(): Promise<{
    storageUsageBytes?: number;
    storageQuotaBytes?: number;
}> {
    try {
        const estimate = await navigator.storage?.estimate?.();
        return { storageUsageBytes: estimate?.usage, storageQuotaBytes: estimate?.quota };
    } catch {
        return {};
    }
}

async function runSweep(): Promise<void> {
    try {
        const store = await getStore();
        if (!store || !isActive()) {
            return;
        }
        const startedAt = Date.now();
        const result = await store.sweep({
            now: startedAt,
            maxAgeMs: PURGE_AFTER_MS,
            maxBytes: MAX_BYTES,
        });
        noteSuccess();
        const storage = await estimateStorage();
        logMapInfo({
            source: 'cache',
            event: 'cache_sweep',
            extraData: { ...asLogFields(result), sweepMs: Date.now() - startedAt, ...storage },
        });
    } catch (error) {
        noteFailure('sweep', error);
    }
}

/**
 * Called once a map has finished drawing. That lets this load's background refreshes go out,
 * and the first call schedules the sweep, at idle, so neither competes with the map.
 */
export function notifyMapDrawn(): void {
    try {
        if (!isActive()) {
            return;
        }
        releaseRefreshes();
        if (sweepScheduled) {
            return;
        }
        sweepScheduled = true;
        const sweep = () => {
            void runSweep();
        };
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(sweep, { timeout: SWEEP_IDLE_TIMEOUT_MS });
        } else {
            setTimeout(sweep, 0);
        }
    } catch {
        // The sweep is housekeeping; skipping it costs nothing this load.
    }
}

export type MapTileCacheUseSnapshot = Pick<Counters, 'hits' | 'misses' | 'expired' | 'bytesServed'>;

export function snapshotMapTileCacheUse(): MapTileCacheUseSnapshot {
    return {
        hits: counters.hits,
        misses: counters.misses,
        expired: counters.expired,
        bytesServed: counters.bytesServed,
    };
}

/**
 * What the cache did since `snapshot`, for `map_drawn`. Approximate when two maps load at
 * once (the FR map and an event preview), since the counters are shared.
 */
export function describeMapTileCacheUseSince(
    snapshot: MapTileCacheUseSnapshot
): Record<string, number | boolean> {
    if (!installed) {
        return { mapTileCacheEnabled: false };
    }
    return {
        mapTileCacheEnabled: true,
        cacheHits: counters.hits - snapshot.hits,
        cacheMisses: counters.misses - snapshot.misses,
        cacheExpired: counters.expired - snapshot.expired,
        cacheBytesServed: counters.bytesServed - snapshot.bytesServed,
    };
}

export type MapTileCache = {
    /** Must come before any other interceptor whose `urls` overlap, since only the first match runs. */
    interceptor: __esri.RequestInterceptor;
    stop: () => void;
};

/**
 * Starts the cache and returns its interceptor for the caller to register.
 *
 * `delegateErrorsTo` is the interceptor this one shadows: the SDK only runs the first
 * interceptor whose `urls` match a request, so failures on URLs that one covers are passed on
 * to its `error`, exactly as if the cache weren't there.
 */
export function startMapTileCache({
    delegateErrorsTo,
}: { delegateErrorsTo?: __esri.RequestInterceptor } = {}): MapTileCache {
    installed = true;
    // Opened now, well before the first map asks for anything, so lookups don't wait on it.
    void getStore();
    const activityTimer = setInterval(reportActivity, ACTIVITY_REPORT_INTERVAL_MS);
    return {
        interceptor: {
            urls: MAP_TILE_CACHE_URLS,
            before: answerFromCache,
            after: storeFromNetwork,
            error: (error: __esri.Error) => routeError(error, delegateErrorsTo),
        },
        stop() {
            installed = false;
            clearInterval(activityTimer);
            clearTimeout(refreshReleaseTimer);
            refreshReleaseTimer = undefined;
        },
    };
}

/**
 * Deletes the cache database, so turning `cad.map_tile_cache.enabled` off frees the disk and
 * gives support a reset. Runs once per app load, and only if this origin ever had a cache.
 */
export async function clearMapTileCache(): Promise<void> {
    if (clearedThisLoad) {
        return;
    }
    clearedThisLoad = true;
    try {
        if (!(await mapTileCacheDatabaseExists())) {
            return;
        }
        // Close this window's own connection first, or the delete waits on it.
        const openStore = await storePromise;
        openStore?.close();
        storePromise = undefined;
        storeReady = false;
        await deleteMapTileCacheDatabase();
        logMapInfo({ source: 'cache', event: 'cache_cleared', extraData: { deleted: true } });
    } catch (error) {
        try {
            logMapInfo({
                source: 'cache',
                event: 'cache_cleared',
                extraData: { deleted: false, errorName: errorName(error) },
            });
        } catch {
            // Logging must never fail the map.
        }
    }
}
